import { useQuery } from "@tanstack/react-query";
import { ConversationRecapService } from "@/services/conversation/ConversationRecapService";
import type { UseConversationRecapOptions } from "@/types/voice/conversation-recap";

/**
 * Fetches an inline recap for a finished conversation. Only fires once the
 * conversation is genuinely over (`enabled`) and there's enough back-and-forth
 * to be worth summarising — short greetings get no recap rather than a hollow one.
 */
export const useConversationRecap = ({ conversationId, transcript, enabled = true }: UseConversationRecapOptions) => {
  const userTurns = transcript.filter((t) => t.role === "user").length;
  const hasEnough = transcript.length >= 4 && userTurns >= 1;

  // The transcript is frozen at conversation end, so a length + last-message key
  // is stable for the lifetime of the wrap-up and won't refetch on re-render.
  const lastMessage = transcript[transcript.length - 1]?.message ?? "";

  return useQuery({
    queryKey: ["conversation-recap", conversationId, transcript.length, lastMessage.slice(0, 48)],
    queryFn: () => ConversationRecapService.generate(transcript),
    enabled: enabled && hasEnough,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 30,
    retry: 1,
  });
};
